"use client";

import { chatQueryKeys } from "@/hooks/keys/chatQueryKeys";
import { useRootContext } from "@/hooks/useRootContext";
import AIChatDark from "@/public/aichat-logo-dark.svg";
import LockSmall from "@/public/lock-small.svg";
import UserSmall from "@/public/user-small.svg";
import login from "@/services/auth";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { useRef } from "react";

export default function LoginPage() {
  const { apiURLBase } = useRootContext();
  const queryClient = useQueryClient();

  const router = useRouter();
  const usernameRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  const loginMutation = useMutation({
    mutationFn: async (params: { username: string; password: string }) => {
      return login(apiURLBase, params);
    },
    onSuccess: () => {
      loginMutation.reset();
      queryClient.invalidateQueries({ queryKey: chatQueryKeys.all() });
      router.push("/chats");
    },
  });

  const handleLoginClick = () => {
    loginMutation.mutate({
      username: usernameRef.current ? usernameRef.current.value : "",
      password: passwordRef.current ? passwordRef.current.value : "",
    });
  };

  return (
    <div className="flex flex-col justify-center items-center w-full h-full bg-white">
      <div className="flex flex-col w-[602px] h-[550px] px-[100px] py-[80px] gap-12 items-center justify-center rounded-xl shadow-[0_0_3px_3px_rgba(0,0,0,0.05)]">
        <div className="flex flex-col gap-3 items-center">
          <Image src={AIChatDark} alt="TeaChat" width={280} height={108} />
          <p className="text-lg font-bold">생성형 AI 챗봇 솔루션</p>
        </div>
        <div className="flex flex-col gap-6 items-center justify-center w-full">
          <div className="flex flex-col justify-center items-center gap-3 w-full">
            <div className="flex items-center px-5 rounded-[5px] h-[64px] gap-3 border border-[#e6e4e1] w-full">
              <Image src={UserSmall} alt="사용자" />
              <input
                type="text"
                ref={usernameRef}
                placeholder="아이디를 입력하세요"
                disabled={loginMutation.isPending}
                className="flex-grow w-full text-lg text-left placeholder:text-gray-500 outline-none"
              />
            </div>
            <div className="flex items-center px-5 rounded-[5px] h-[64px] gap-3 border border-[#e6e4e1] w-full">
              <Image src={LockSmall} alt="비밀번호" />
              <input
                type="password"
                ref={passwordRef}
                placeholder="비밀번호를 입력하세요"
                disabled={loginMutation.isPending}
                className="w-full text-lg text-left placeholder:text-gray-500 outline-none"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleLoginClick();
                  }
                }}
              ></input>
            </div>
            <div className="flex justify-center items-center h-[20px] pt-4 text-red-500 text-center w-full">
              {loginMutation.error &&
                (loginMutation.error.message || "로그인에 실패하였습니다.")}
            </div>
          </div>
          <button
            className="flex justify-center w-full px-5 py-4 rounded-[5px] bg-[#6191F1] cursor-pointer"
            onClick={() => handleLoginClick()}
          >
            <p className="text-xl text-center text-white">로그인</p>
          </button>
        </div>
      </div>
    </div>
  );
}
